import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import Theme from '../../styles/Theme/Theme';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  z-index: 9000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Modal = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: 32px;
  width: 480px;
  max-width: calc(100vw - 40px);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: ${Theme.colors?.['coolgray-12'] || '#222'};
`;

const Hint = styled.p`
  margin: -12px 0 0;
  font-size: 13px;
  color: ${Theme.colors?.['coolgray-7'] || '#97999B'};
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: ${Theme.colors?.['coolgray-11'] || '#53565A'};
`;

const Input = styled.input`
  height: 40px;
  padding: 0 12px;
  border: 1.5px solid ${p => p.$error
    ? '#DC2626'
    : (Theme.colors?.['neutral-3'] || '#D1D8DC')};
  border-radius: 8px;
  font-size: 14px;
  color: ${Theme.colors?.['coolgray-12'] || '#222'};
  outline: none;
  transition: border-color 0.15s;
  &:focus {
    border-color: ${p => p.$error ? '#DC2626' : (Theme.colors?.['green-deloitte'] || '#86BC25')};
  }
`;

const ErrorMsg = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: #DC2626;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 4px;
`;

const CancelBtn = styled.button`
  padding: 10px 18px;
  border-radius: 8px;
  border: 1.5px solid ${Theme.colors?.['neutral-3'] || '#D1D8DC'};
  background: #fff;
  color: ${Theme.colors?.['coolgray-11'] || '#53565A'};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  &:hover { background: ${Theme.colors?.['neutral-1'] || '#F5F8FA'}; }
`;

const ConfirmBtn = styled.button`
  padding: 10px 22px;
  border-radius: 8px;
  border: none;
  background: ${Theme.colors?.['green-deloitte'] || '#86BC25'};
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  &:disabled { opacity: 0.45; cursor: not-allowed; }
  &:not(:disabled):hover { filter: brightness(1.08); }
`;

function isValidUrl(str) {
  try {
    const u = new URL(str);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Modal dialog for adding a custom URL widget.
 * Props:
 *   onConfirm({ name, url }) — called when the user submits
 *   onClose()               — called when the user cancels / presses Escape
 */
const AddUrlWidgetModal = ({ onConfirm, onClose }) => {
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const nameError = submitted && name.trim().length === 0
    ? 'Por favor introduz um nome para o widget'
    : null;

  const urlError = submitted && !isValidUrl(url.trim())
    ? 'Por favor introduz um URL válido (http:// ou https://)'
    : null;

  const handleConfirm = useCallback(() => {
    setSubmitted(true);
    if (name.trim().length === 0 || !isValidUrl(url.trim())) return;
    onConfirm({ name: name.trim(), url: url.trim() });
  }, [name, url, onConfirm]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') handleConfirm();
    if (e.key === 'Escape') onClose();
  }, [handleConfirm, onClose]);

  return (
    <Overlay onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <Modal onKeyDown={handleKeyDown}>
        <Title>Adicionar Widget de URL</Title>
        <Hint>Adiciona um website, vídeo YouTube ou qualquer outra fonte online.</Hint>

        <Field>
          Nome do widget
          <Input
            autoFocus
            placeholder="Ex: Câmara Aeroporto"
            value={name}
            $error={!!nameError}
            onChange={e => setName(e.target.value)}
          />
          {nameError && <ErrorMsg>{nameError}</ErrorMsg>}
        </Field>

        <Field>
          URL da fonte
          <Input
            placeholder="https://... ou URL do YouTube"
            value={url}
            $error={!!urlError}
            onChange={e => setUrl(e.target.value)}
          />
          {urlError && <ErrorMsg>{urlError}</ErrorMsg>}
        </Field>

        <Actions>
          <CancelBtn onClick={onClose}>Cancelar</CancelBtn>
          <ConfirmBtn onClick={handleConfirm}>Criar Widget</ConfirmBtn>
        </Actions>
      </Modal>
    </Overlay>
  );
};

export default AddUrlWidgetModal;
